import React, { useEffect, useState } from 'react';
import ModalInc from '../../../shared-ui/ModalInc';
import { ValidatorForm, TextValidator, SelectValidator } from 'react-material-ui-form-validator';
import { Autocomplete, Grid, TextField } from '@mui/material';
import ButtonInc from '../../../shared-ui/ButtonInc';
import DropzoneS3Uploader from 'react-dropzone-s3-uploader';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import ProgressState from './ProgressState/ProgressState';
import {
  emailEntityAttachments,
  emailNotifyRestrictions,
  emailTypesEntity,
} from '../../../lib/constants';
import {
  getS3BucketSignedUrlForEmailTemplatesV2,
  getTemplateDocuments,
} from '../../../lib/requests.es6';
import { useStyle } from "./Style";
import uniqueCodes from '../../SmsTemplate/template_codes';

const EditEmailTemplate = (props) => {
  const styles = useStyle();
  const [s3Url] = useState('https://s3.eu-west-1.amazonaws.com/partner-email-templates');

  const [formData, setFormData] = useState({
    guid: '',
    code: '',
    language: 'en',
    subject: '',
    filename: '',
    active: true,
    recipients: '',
    cc_recipients: '',
    attachments: [],
    entity_attachments: [],
    entity_type: '',
    notify_restriction: '',
  });
  const [documentOptions, setDocumentOptions] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [progress, setProgress] = useState(0);
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(function () {
    if (!props.open) {
      return;
    }

    setLoadingDocuments(true);
    getTemplateDocuments()
      .then(function (response) {
        setDocumentOptions(response.data || []);
      })
      .catch(function () {
        setDocumentOptions([]);
      })
      .finally(function () {
        setLoadingDocuments(false);
      });
  }, [props.open]);

  useEffect(() => {
    if (!props.template) {
      return;
    }

    setFormData({
      guid: props.template.guid || '',
      code: props.template.code || '',
      language: props.template.language || 'en',
      subject: props.template.subject || '',
      filename: props.template.filename || '',
      active: props.template.active === false ? false : true,
      recipients: (props.template.recipients || []).join(', '),
      cc_recipients: (props.template.cc_recipients || []).join(', '),
      attachments: props.template.attachments || [],
      entity_attachments: props.template.entity_attachments || [],
      entity_type: props.template.entity_type || '',
      notify_restriction: props.template.notify_restriction || '',
    });
  }, [props.template]);

  // The backend stores values the frontend option lists do not know about
  // (e.g. entity_type "Content"). Keep the stored one selectable so editing a
  // template never silently drops it.
  const withStoredValue = function (options, value) {
    if (!value) {
      return options;
    }

    var known = options.some(function (option) {
      return option.value === value;
    });

    return known ? options : options.concat([{ value: value, label: value }]);
  };

  const parseCsvToArray = function (value) {
    if (!value) {
      return [];
    }

    return value
      .split(',')
      .map(function (item) {
        return item.trim();
      })
      .filter(function (item) {
        return item.length > 0;
      });
  };

  const handleChange = function (event) {
    var name = event.target.name;
    var value = event.target.value;
    if (
      (name === 'attachments' || name === 'entity_attachments') &&
      typeof value === 'string'
    ) {
      value = value.split(',');
    }
    setFormData(function (prevData) {
      return {
        ...prevData,
        [name]: value,
      };
    });
  };

  const getS3Ur = function (file, callback) {
    getS3BucketSignedUrlForEmailTemplatesV2(file, callback);
  };

  const uploadOptions = {
    getSignedUrl: getS3Ur,
    s3Url: s3Url,
    uploadRequestHeaders: {},
    onProgress: function (nextProgress) {
      setProgress(nextProgress);
    },
    onError: function (nextError) {
      setErrorMessage(nextError);
    },
  };

  const handleFinishedUpload = function (info) {
    var uploadedFilename = info.bucketKey;
    setFormData(function (prevData) {
      return {
        ...prevData,
        filename: uploadedFilename,
      };
    });
  };

  const submitUpdate = function () {
    props.onSave({
      guid: formData.guid,
      code: formData.code.trim(),
      language: formData.language.trim(),
      subject: formData.subject.trim(),
      filename: formData.filename.trim(),
      active: formData.active === true,
      recipients: parseCsvToArray(formData.recipients),
      cc_recipients: parseCsvToArray(formData.cc_recipients),
      attachments: formData.attachments,
      entity_attachments: formData.entity_attachments,
      entity_type: formData.entity_type,
      notify_restriction: formData.notify_restriction,
    });
  };

  var attachmentSelectionLabel = 'Select attachments from documents';
  if (loadingDocuments) {
    attachmentSelectionLabel = 'Loading document templates...';
  } else if (!documentOptions.length) {
    attachmentSelectionLabel = 'No document templates available';
  }

  return (
    <ModalInc
      modalTitle="Edit Email Template"
      subTitle="Update template details"
      open={props.open}
      onClose={props.onClose}
      fullWidth
      maxWidth="md"
    >
      <ValidatorForm onSubmit={submitUpdate}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <small style={{ color: '#777' }}>
              Fields marked with * are required.
            </small>
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <Autocomplete
              freeSolo
              clearOnBlur={false}
              selectOnBlur={false}
              options={uniqueCodes}
              value={formData.code}
              onChange={function (event, newValue) {
                if (newValue === null || newValue === undefined) {
                  return;
                }
                setFormData(function (prevData) {
                  return {
                    ...prevData,
                    code: String(newValue),
                  };
                });
              }}
              onInputChange={function (event, newInputValue, reason) {
                if (reason === 'input' || reason === 'clear') {
                  setFormData(function (prevData) {
                    return {
                      ...prevData,
                      code: newInputValue || '',
                    };
                  });
                }
              }}
              renderInput={function (params) {
                return (
                  <TextField
                    {...params}
                    id="code"
                    label="Template Code *"
                    placeholder="Code e.g. KYC_UPDATE"
                    variant="outlined"
                    fullWidth
                    helperText={
                      'Pick a suggested code or type your own event code.'
                    }
                  />
                );
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <TextValidator
              name="language"
              label="Language *"
              value={formData.language}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              validators={['required']}
              errorMessages={['Please enter language']}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <TextValidator
              name="subject"
              label="Email Subject *"
              value={formData.subject}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              validators={['required']}
              errorMessages={['Please enter subject']}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={6}>
            <TextValidator
              name="filename"
              label="Body HTML Filename *"
              value={formData.filename}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              validators={['required']}
              errorMessages={['Please enter filename']}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={12}>
            <div className={styles.uploadsection}>
              <small style={{ color: '#777' }}>
                Replace body HTML (optional)
              </small>
              <DropzoneS3Uploader
                onFinish={handleFinishedUpload}
                upload={uploadOptions}
                s3Url={s3Url}
                passChildrenProps={false}
                maxSize={1024 * 1024 * 500}
                className={styles.uploadzone}
                style={{
                  width: '100%',
                  height: '160px',
                  background: '#FFFFFF',
                  border: '1px dashed #B8B8B8',
                  marginTop: '8px',
                  padding: '32px 0',
                }}
              >
                <div>
                  <ProgressState progress={progress} errorMessage={errorMessage} />
                </div>
              </DropzoneS3Uploader>
            </div>
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <SelectValidator
              name="active"
              value={formData.active}
              label="Active"
              onChange={handleChange}
              variant="outlined"
              style={{ width: '100%' }}
            >
              <MenuItem value={true}>Yes</MenuItem>
              <MenuItem value={false}>No</MenuItem>
            </SelectValidator>
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <SelectValidator
              name="entity_type"
              value={formData.entity_type}
              label="Entity Type *"
              onChange={handleChange}
              variant="outlined"
              validators={['required']}
              errorMessages={['Please select entity type']}
              style={{ width: '100%' }}
            >
              {withStoredValue(emailTypesEntity, formData.entity_type).map(function (type, index) {
                return (
                  <MenuItem key={index} value={type.value}>
                    {type.label}
                  </MenuItem>
                );
              })}
            </SelectValidator>
          </Grid>

          <Grid item xs={12} sm={6} md={6}>
            <SelectValidator
              name="notify_restriction"
              value={formData.notify_restriction}
              label="Notify Restriction *"
              onChange={handleChange}
              variant="outlined"
              validators={['required']}
              errorMessages={['Please select notify restriction']}
              style={{ width: '100%' }}
            >
              {withStoredValue(emailNotifyRestrictions, formData.notify_restriction).map(function (type, index) {
                return (
                  <MenuItem key={index} value={type.value}>
                    {type.label}
                  </MenuItem>
                );
              })}
            </SelectValidator>
          </Grid>

          <Grid item xs={12} sm={6} md={6}>
            <TextValidator
              name="recipients"
              label="Recipients"
              value={formData.recipients}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              helperText="Optional extra addresses on top of the notify restriction. Comma separated."
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <TextValidator
              name="cc_recipients"
              label="CC Recipients"
              value={formData.cc_recipients}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              helperText="optional comma separated values"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="edit-attachments-label">Attachments</InputLabel>
              <Select
                labelId="edit-attachments-label"
                id="edit-attachments"
                multiple
                name="attachments"
                value={formData.attachments}
                onChange={handleChange}
                label="Attachments"
                renderValue={function (selected) {
                  if (!selected || !selected.length) {
                    return 'None';
                  }
                  return selected.join(', ');
                }}
              >
                {documentOptions.map(function (doc, index) {
                  var value = doc.guid || doc.code || '';
                  if (!value) {
                    return null;
                  }
                  var label = doc.name || doc.code || doc.guid;
                  return (
                    <MenuItem key={index} value={value}>
                      {label}
                    </MenuItem>
                  );
                })}
              </Select>
              <small style={{ color: '#777', marginTop: '4px' }}>
                {attachmentSelectionLabel}
              </small>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="edit-entity-attachments-label">
                Entity Attachments
              </InputLabel>
              <Select
                labelId="edit-entity-attachments-label"
                id="edit-entity-attachments"
                multiple
                name="entity_attachments"
                value={formData.entity_attachments}
                onChange={handleChange}
                label="Entity Attachments"
                renderValue={function (selected) {
                  if (!selected || !selected.length) {
                    return 'None';
                  }
                  return selected.join(', ');
                }}
              >
                {emailEntityAttachments.map(function (type, index) {
                  return (
                    <MenuItem key={index} value={type.value}>
                      {type.label}
                    </MenuItem>
                  );
                })}
              </Select>
              <small style={{ color: '#777', marginTop: '4px' }}>
                Optional entity-generated attachments.
              </small>
            </FormControl>
          </Grid>
        </Grid>

        <Grid container spacing={2} style={{ marginTop: '12px' }}>
          <Grid item xs={12} sm={6} md={6}>
            <ButtonInc
              variant="contained"
              color="primary"
              size="large"
              type="submit"
              loading={props.loading}
              disabled={props.loading}
              style={{ width: '100%' }}
            >
              Save Changes
            </ButtonInc>
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <ButtonInc
              variant="contained"
              size="large"
              onClick={props.onClose}
              disabled={props.loading}
              style={{ width: '100%', background: '#FBA92D' }}
            >
              Cancel
            </ButtonInc>
          </Grid>
        </Grid>
      </ValidatorForm>
    </ModalInc>
  );
};

export default EditEmailTemplate;
